import { ChangeEvent, useState } from 'react';

import Alert from '@/components/Alert';
import Button from '@/components/Button';
import Card from '@/components/Card';
import FormElement from '@/components/FormElement';
import Modal from '@/components/Modal';
import Stack from '@/components/Stack';
import TextArea from '@/components/TextArea';
import TextInput from '@/components/TextInput';
import TextSelect from '@/components/TextSelect';
import { Body } from '@/components/Typography';
import { NoshowAlert, ResolutionType } from '@/types/noshowAlerts';

const RESOLUTION_OPTIONS: { value: ResolutionType; label: string }[] = [
  { value: 'no_response', label: 'No Response' },
  { value: 'backfill_created', label: 'Backfill Created' },
  { value: 'backfill_declined', label: 'Backfill Declined' },
];

interface ResolveModalProps {
  alert: NoshowAlert;
  onClose: () => void;
  onResolve: (
    id: number,
    notes: string,
    resolutionType: ResolutionType,
    backfillJobUrl?: string,
  ) => Promise<void>;
}

const ResolveModal = ({ alert, onClose, onResolve }: ResolveModalProps) => {
  const [notes, setNotes] = useState('');
  const [resolutionType, setResolutionType] = useState<ResolutionType | ''>('');
  const [backfillJobUrl, setBackfillJobUrl] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const isBackfillCreated = resolutionType === 'backfill_created';
  const isValid =
    resolutionType !== '' &&
    (!isBackfillCreated || backfillJobUrl.trim() !== '');

  const handleSubmit = async () => {
    if (!isValid) return;

    setLoading(true);
    setError(null);
    try {
      await onResolve(
        alert.id,
        notes,
        resolutionType as ResolutionType,
        isBackfillCreated ? backfillJobUrl.trim() : undefined,
      );
      onClose();
    } catch (err: any) {
      setError(err.response?.data?.errors?.[0] || 'Failed to resolve alert');
      setLoading(false);
    }
  };

  return (
    <Modal
      disableClickout
      size="sm"
      title={`Resolve Alert #${alert.id}`}
      onRequestClose={onClose}
    >
      <Card.Section>
        {error && (
          <Alert
            description={error}
            icon="warning"
            status="danger"
            title="Error"
          />
        )}
        <Stack vertical gap={8}>
          <Body weight="medium">Worker: {alert.worker?.name || 'N/A'}</Body>
          <Body weight="medium">
            Supervisor: {alert.supervisor?.name || 'N/A'}
          </Body>
          <Body weight="medium">Customer: {alert.customer_name || 'N/A'}</Body>
        </Stack>
        <FormElement label="Resolution" required>
          <TextSelect
            value={resolutionType}
            onChange={(e: ChangeEvent<HTMLSelectElement>) =>
              setResolutionType(e.target.value as ResolutionType | '')
            }
          >
            <option value="" disabled>
              Select resolution...
            </option>
            {RESOLUTION_OPTIONS.map((opt) => (
              <option key={opt.value} value={opt.value}>
                {opt.label}
              </option>
            ))}
          </TextSelect>
        </FormElement>
        {isBackfillCreated && (
          <FormElement label="Backfill Job URL" required>
            <TextInput
              placeholder="https://..."
              value={backfillJobUrl}
              onChange={(e: ChangeEvent<HTMLInputElement>) =>
                setBackfillJobUrl(e.target.value)
              }
            />
          </FormElement>
        )}
        <FormElement label="Notes">
          <TextArea
            placeholder="Add resolution notes..."
            value={notes}
            onChange={(e) => setNotes(e.target.value)}
          />
        </FormElement>
      </Card.Section>
      <Card.Section>
        <Stack justify="end">
          <Button
            a11yLabel="Cancel"
            appearance="outline"
            label="Cancel"
            onClick={onClose}
          />
          <Button
            a11yLabel="Resolve"
            disabled={!isValid}
            isLoading={loading}
            label="Resolve"
            onClick={handleSubmit}
          />
        </Stack>
      </Card.Section>
    </Modal>
  );
};

export default ResolveModal;
